import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startGoogleLogin = useCallback((redirectPath = "/") => {
    try {
      sessionStorage.setItem("postLoginRedirect", redirectPath);
    } catch {
      /* ignore storage errors */
    }
    const safePath = redirectPath && redirectPath.startsWith("/") && !redirectPath.startsWith("//") ? redirectPath : "/";
    window.location.href = `/api/auth/google/start?redirect=${encodeURIComponent(safePath)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing session anyway:", formatApiError(e));
    }
    setUser(false);
  }, []);

  const isAdmin = !!user && user.role === "admin";
  const isContributor = !!user && (user.role === "admin" || user.role === "member");

  const canEdit = useCallback(
    (runbook) => {
      if (!user || !runbook) return false;
      if (user.role === "admin") return true;
      return Boolean(runbook.created_by) && runbook.created_by === user.email;
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      isAdmin,
      isContributor,
      canEdit,
      startGoogleLogin,
      logout,
      refresh,
      formatApiError,
    }),
    [user, isAdmin, isContributor, canEdit, startGoogleLogin, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
